import type { ComputedRef, Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { ResultItem } from '@/utils/file';
import type {
  InitialData as SuburbsSearchSearchFormInitialData,
  ValidData as SuburbsSearchSearchFormValidData,
} from '@/components/searchForms/SuburbsSearchSearchForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import _isEqual from 'lodash/isEqual';

import * as routeNames from '@/constants/routeNames';

export interface SuburbsSearchViewComposable {
  search: (formData: SuburbsSearchSearchFormValidData) => void;
  initialData: ComputedRef<SuburbsSearchSearchFormInitialData>;
  total: Readonly<Ref<number>>;
  resultsForCsv: Readonly<Ref<ReadonlyDeep<ResultItem[]>>>;
}

export default function useSuburbsSearchView(
  querySuburb: Readonly<Ref<string | undefined>>,
  queryPostCode: Readonly<Ref<number | undefined>>,
  suburbs: Readonly<
    Ref<
      | ReadonlyDeep<openapi.AddressBookGnafSuburbLookupListContentOpenapi>
      | undefined
    >
  >,
): SuburbsSearchViewComposable {
  const router = useRouter();
  const route = useRoute();

  const initialData = computed<SuburbsSearchSearchFormInitialData>(() => {
    return {
      suburbGuess: querySuburb.value,
      postCode: queryPostCode.value,
    };
  });

  const total = computed(() => {
    if (!Array.isArray(suburbs.value)) return 0;
    return suburbs.value.length;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    if (!Array.isArray(suburbs.value)) return [];
    return suburbs.value;
  });

  function search(formData: SuburbsSearchSearchFormValidData): void {
    const currentQuery = {
      suburb: undefined,
      postcode: undefined,
      ...route.query,
    };
    const query = {
      ...route.query,
      suburb: formData.suburbGuess,
      postcode: formData.postCode?.toString(),
    };
    const isReplace = _isEqual(currentQuery, query);
    void router.push({
      name: routeNames.SEARCH_SUBURBS,
      query,
      force: true,
      replace: isReplace,
    });
  }

  return {
    search,
    initialData,
    total,
    resultsForCsv,
  };
}
